import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useGeneralExams, useActiveSessions, useSessionHistory, useDeleteSession, useCompleteSession, useExamSections } from '../lib/api';
import type { StudySession } from '../lib/api';

export function ExamSessionsPage() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { data: examsData } = useGeneralExams();
  const { data: sectionsData } = useExamSections(examId ?? null);
  const { data: sessionsData } = useActiveSessions({ examId: examId ?? null });
  const { data: historyData } = useSessionHistory({ examId: examId ?? null });
  const deleteSession = useDeleteSession();
  const completeSession = useCompleteSession();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const exam = examsData?.exams?.find(e => e.id === examId);
  const sections = sectionsData?.sections ?? [];
  const activeSessions = sessionsData?.sessions ?? [];
  const pastSessions = historyData?.sessions ?? [];

  const resumeSession = (session: StudySession) => {
    const sectionId = session.examSectionId ?? session.classSectionId;
    if (!sectionId) return;
    navigate(`/study/exam/${sectionId}?session=${session.id}`);
  };

  return (
    <div className="max-w-lg mx-auto px-4 pb-20">
      {/* Header */}
      <div className="flex items-center gap-3 pt-2 mb-5">
        <Link to={`/exam/${examId}`} className="text-squishy-primary">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
        </Link>
        <div>
          <h1 className="text-xl font-bold text-squishy-dark">All Sessions</h1>
          {exam && <p className="text-xs text-slate-500">{exam.code}</p>}
        </div>
      </div>

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div className="space-y-2 mb-6">
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">In Progress</h2>
          {activeSessions.map(session => (
            <SessionCardFull
              key={session.id}
              session={session}
              sections={sections}
              onResume={() => resumeSession(session)}
              onDelete={() => setConfirmDeleteId(session.id)}
            />
          ))}
        </div>
      )}

      {/* Completed Sessions */}
      {pastSessions.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Completed</h2>
          {pastSessions.map(s => {
            const accuracy = s.questionsAnswered > 0 ? Math.round((s.correctCount / s.questionsAnswered) * 100) : 0;
            const section = sections.find(sec => sec.id === s.examSectionId);
            const date = s.endedAt ? new Date(s.endedAt).toLocaleDateString() : new Date(s.startedAt).toLocaleDateString();

            return (
              <div key={s.id} className="bg-white border rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  {section && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">{section.name}</span>}
                  <span className="text-[10px] text-slate-400">{date}</span>
                </div>
                <div className="text-sm font-medium text-squishy-dark">{s.label || 'All Topics'}</div>
                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400">
                  <span>{s.questionsAnswered} answered</span>
                  <span>{'\u00B7'}</span>
                  <span className={accuracy >= 70 ? 'text-green-600' : accuracy >= 50 ? 'text-amber-500' : 'text-red-500'}>{accuracy}% correct</span>
                  <span>{'\u00B7'}</span>
                  <span className="text-emerald-600">+{s.xpEarned} XP</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeSessions.length === 0 && pastSessions.length === 0 && (
        <div className="text-center py-12 text-slate-400 text-sm">No sessions yet. Start studying!</div>
      )}

      {/* ── Delete Session Confirmation ─────────────────────────── */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6 animate-fade-in">
          <div className="bg-white rounded-2xl p-5 w-full max-w-xs shadow-xl text-center space-y-4 animate-slide-up">
            <div className="text-3xl">&#x1F5D1;</div>
            <p className="text-sm font-semibold text-squishy-navy">Discard this session?</p>
            <p className="text-xs text-slate-500">Your progress will be lost and can't be recovered.</p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition active:scale-[0.97]"
              >
                Keep
              </button>
              <button
                onClick={() => { deleteSession.mutate(confirmDeleteId); setConfirmDeleteId(null); }}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition active:scale-[0.97]"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SessionCardFull({ session, sections, onResume, onDelete }: {
  session: StudySession;
  sections: any[];
  onResume: () => void;
  onDelete: () => void;
}) {
  const progress = session.totalQuestions > 0
    ? Math.round((session.currentIndex / session.totalQuestions) * 100)
    : 0;
  const accuracy = session.questionsAnswered > 0
    ? Math.round((session.correctCount / session.questionsAnswered) * 100)
    : 0;
  const remaining = session.totalQuestions - session.currentIndex;
  const section = sections.find(s => s.id === session.examSectionId);

  const diff = Date.now() - new Date(session.startedAt).getTime();
  const mins = Math.floor(diff / 60000);
  const timeAgo = mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : Math.floor(mins / 60) < 24 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;

  return (
    <div className="bg-white border rounded-xl p-4 active:bg-slate-50 transition-colors">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onResume}>
          <div className="flex items-center gap-2 mb-1">
            {section && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">{section.name}</span>}
            <span className="text-[10px] text-slate-400">{timeAgo}</span>
          </div>
          <div className="text-sm font-medium text-squishy-dark truncate">{session.label || 'All Topics'}</div>
          <div className="flex items-center gap-3 mt-2">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-teal-400 to-emerald-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-[10px] text-slate-500 shrink-0 font-medium">{progress}%</span>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400">
            <span>{remaining} left</span>
            <span>{'\u00B7'}</span>
            <span>{accuracy}% correct</span>
            <span>{'\u00B7'}</span>
            <span>+{session.xpEarned} XP</span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button onClick={onResume} className="bg-squishy-primary text-white px-3 py-1.5 rounded-lg text-xs font-semibold active:scale-95 transition-transform">Resume</button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-slate-400 hover:text-red-500 text-[10px] text-center transition-colors">Discard</button>
        </div>
      </div>
    </div>
  );
}
